import type {AriaButtonProps} from '@react-types/button';

import type {AriaComboBoxProps} from '@react-types/combobox';

import type {
  BaseEvent,
  DOMAttributes,
  KeyboardDelegate,
  MultipleSelection,
  PressEvent,
} from '@react-types/shared';

import type {
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  RefObject,
  TouchEvent,
} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {getChildNodes, getItemCount} from '../../@react-stately/collections';
import {
  chain,
  isAppleDevice,
  mapSelectionToKeysSet,
  mergeProps,
  useLabels,
  useRouter,
} from '../utils';
import {announce} from '../live-announcer';
import {useLocalizedStringFormatter} from '../i18n';
import {ListKeyboardDelegate, useSelectableCollection} from '../selection';

import {ariaHideOutside} from '../overlays';
import {useMenuTrigger} from '../../@react-aria/menu';
import type {MultiComboBoxState} from '../../@react-stately/combobox';
import {getItemId, listData} from '../../@react-aria/listbox';
import {useTextField} from '../../@react-aria/textfield';
import type {AriaListBoxOptions} from '../../@react-aria/listbox';
import intlMessages from './intl';

export interface AriaComboBoxOptions<T>
  extends Omit<
      AriaComboBoxProps<T>,
      'children' | 'selectedKey' | 'defaultSelectedKey' | 'onSelectionChange'
    >,
    Omit<MultipleSelection, 'disallowEmptySelection'> {
  /** The ref for the input element. */
  inputRef: RefObject<HTMLInputElement>;
  /** The ref for the list box popover. */
  popoverRef: RefObject<Element>;
  /** The ref for the list box. */
  listBoxRef: RefObject<HTMLElement>;
  /** The ref for the optional list box popup trigger button.  */
  buttonRef?: RefObject<Element>;
  /** An optional keyboard delegate implementation, to override the default. */
  keyboardDelegate?: KeyboardDelegate;
}

export interface ComboBoxAria<T> {
  /** Props for the label element. */
  labelProps: DOMAttributes;
  /** Props for the combo box input element. */
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  /** Props for the list box, to be passed to [useListBox](useListBox.html). */
  listBoxProps: AriaListBoxOptions<T>;
  /** Props for the optional trigger button, to be passed to [useButton](useButton.html). */
  buttonProps: AriaButtonProps;
  /** Props for the combo box description element, if any. */
  descriptionProps: DOMAttributes;
  /** Props for the combo box error message element, if any. */
  errorMessageProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a combo box component.
 * A combo box combines a text input with a listbox, allowing users to filter a list of options to items matching a query.
 * @param props - Props for the combo box.
 * @param state - State for the select, as returned by `useComboBoxState`.
 */
export function useComboBox<T>(
  props: AriaComboBoxOptions<T>,
  state: MultiComboBoxState,
): ComboBoxAria<T> {
  const {
    buttonRef,
    popoverRef,
    inputRef,
    listBoxRef,
    keyboardDelegate,
    shouldFocusWrap,
    isReadOnly,
    isDisabled,
  } = props;

  const stringFormatter = useLocalizedStringFormatter(intlMessages);
  const {menuTriggerProps, menuProps} = useMenuTrigger<T>(
    {
      type: 'listbox',
      isDisabled: isDisabled || isReadOnly,
    },
    state,
    // @ts-expect-error - react-aria has weaker type checking, the buttonRef might be undefined
    buttonRef,
  );

  // Set listbox id so it can be used when calling getItemId later
  // @ts-expect-error - react-aria has weaker type checking, the menuProps.id might be undefined
  listData.set(state, {id: menuProps.id});

  // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.
  const delegate = useMemo(
    () =>
      keyboardDelegate ||
      new ListKeyboardDelegate(
        state.collection,
        state.disabledKeys,
        listBoxRef,
      ),
    [keyboardDelegate, state.collection, state.disabledKeys, listBoxRef],
  );

  // Use useSelectableCollection to get the keyboard handlers to apply to the textfield
  const {collectionProps} = useSelectableCollection({
    selectionManager: state.selectionManager,
    keyboardDelegate: delegate,
    disallowTypeAhead: true,
    disallowEmptySelection: true,
    shouldFocusWrap,
    ref: inputRef,
    // Prevent item scroll behavior from being applied here, should be handled in the user's Popover + ListBox component
    isVirtualized: true,
    shouldUseVirtualFocus: true,
  });

  const router = useRouter();

  // For textfield specific keydown operations
  const onKeyDown = (e: BaseEvent<KeyboardEvent<HTMLInputElement>>) => {
    switch (e.key) {
      case 'Enter':
      case 'Tab':
        // Prevent form submission if menu is open since we may be selecting a option
        if (state.isOpen && e.key === 'Enter') {
          e.preventDefault();
        }

        // If the focused item is a link, trigger opening it. Items that are links are not selectable.
        if (
          state.isOpen &&
          state.selectionManager.focusedKey != null &&
          state.selectionManager.isLink(state.selectionManager.focusedKey)
        ) {
          if (e.key === 'Enter') {
            const item = listBoxRef.current?.querySelector(
              `[data-key="${state.selectionManager.focusedKey}"]`,
            );
            if (item instanceof HTMLAnchorElement) {
              router.open(item, e);
            }
          }

          state.close();
        } else {
          state.commit();
        }
        break;
      case 'Escape':
        const hasSelection =
          state.selectedKeys && Array.from(state.selectedKeys).length > 0;
        if (
          hasSelection ||
          state.inputValue === '' ||
          props.allowsCustomValue
        ) {
          e.continuePropagation();
        }
        state.revert();
        break;
      case 'ArrowDown':
        state.open('first', 'manual');
        break;
      case 'ArrowUp':
        state.open('last', 'manual');
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        state.selectionManager.setFocusedKey(null);
        break;
      default:
        break;
    }
  };

  const onBlur = (e: FocusEvent<Element>) => {
    // Ignore blur if focused moved to the button or into the popover.
    if (
      e.relatedTarget === buttonRef?.current ||
      popoverRef.current?.contains(e.relatedTarget as Node | null)
    ) {
      return;
    }

    if (props.onBlur) {
      props.onBlur(e as FocusEvent<HTMLInputElement>);
    }

    state.setFocused(false);
  };

  const onFocus = (e: FocusEvent<Element>) => {
    if (state.isFocused) {
      return;
    }

    if (props.onFocus) {
      props.onFocus(e as FocusEvent<HTMLInputElement>);
    }

    state.setFocused(true);
  };

  const {labelProps, inputProps, descriptionProps, errorMessageProps} =
    useTextField(
      {
        ...props,
        onChange: state.setInputValue,
        onKeyDown: !isReadOnly
          ? chain(
              state.isOpen && collectionProps.onKeyDown,
              onKeyDown,
              props.onKeyDown,
            )
          : undefined,
        onBlur,
        value: state.inputValue,
        onFocus,
        autoComplete: 'off',
      },
      inputRef,
    );

  // Press handlers for the ComboBox button
  const onPress = (e: PressEvent) => {
    if (e.pointerType === 'touch') {
      // Focus the input field in case it isn't focused yet
      inputRef.current?.focus();
      state.toggle(null, 'manual');
    }
  };

  const onPressStart = (e: PressEvent) => {
    if (e.pointerType !== 'touch') {
      inputRef.current?.focus();
      state.toggle(
        e.pointerType === 'keyboard' || e.pointerType === 'virtual'
          ? 'first'
          : null,
        'manual',
      );
    }
  };

  const triggerLabelProps = useLabels({
    id: menuTriggerProps.id,
    'aria-label': stringFormatter.format('buttonLabel'),
    'aria-labelledby': props['aria-labelledby'] || labelProps.id,
  });

  const listBoxProps = useLabels({
    id: menuProps.id,
    'aria-label': stringFormatter.format('listboxLabel'),
    'aria-labelledby': props['aria-labelledby'] || labelProps.id,
  });

  // If a touch happens on direct center of ComboBox input, might be virtual click from iPad so open ComboBox menu
  const lastEventTime = useRef(0);
  const onTouchEnd = (e: TouchEvent) => {
    if (isDisabled || isReadOnly) {
      return;
    }

    // Sometimes VoiceOver on iOS fires two touchend events in quick succession. Ignore the second one.
    if (e.timeStamp - lastEventTime.current < 500) {
      e.preventDefault();
      inputRef.current?.focus();
      return;
    }

    const rect = (e.target as Element).getBoundingClientRect();
    const touch = e.changedTouches[0];

    const centerX = Math.ceil(rect.left + 0.5 * rect.width);
    const centerY = Math.ceil(rect.top + 0.5 * rect.height);

    if (touch.clientX === centerX && touch.clientY === centerY) {
      e.preventDefault();
      inputRef.current?.focus();
      state.toggle(null, 'manual');

      lastEventTime.current = e.timeStamp;
    }
  };

  // VoiceOver has issues with announcing aria-activedescendant properly on change
  // (especially on iOS). We use a live region announcer to announce focus changes
  // manually. In addition, section titles are announced when navigating into a new section.
  const focusedItem =
    state.selectionManager.focusedKey != null && state.isOpen
      ? state.collection.getItem(state.selectionManager.focusedKey)
      : undefined;
  const sectionKey = focusedItem?.parentKey ?? null;
  const itemKey = state.selectionManager.focusedKey ?? null;
  const lastSection = useRef(sectionKey);
  const lastItem = useRef(itemKey);
  useEffect(() => {
    if (
      isAppleDevice() &&
      focusedItem != null &&
      itemKey !== lastItem.current
    ) {
      const isSelected = state.selectionManager.isSelected(itemKey);
      const section =
        sectionKey != null ? state.collection.getItem(sectionKey) : null;
      const sectionTitle =
        section?.['aria-label'] ||
        (typeof section?.rendered === 'string' ? section.rendered : '') ||
        '';

      const announcement = stringFormatter.format('focusAnnouncement', {
        isGroupChange: !!section && sectionKey !== lastSection.current,
        groupTitle: sectionTitle,
        groupCount: section
          ? [...getChildNodes(section, state.collection)].length
          : 0,
        optionText: focusedItem['aria-label'] || focusedItem.textValue || '',
        isSelected,
      });

      announce(announcement);
    }

    lastSection.current = sectionKey;
    lastItem.current = itemKey;
  });

  // Announce the number of available suggestions when it changes
  const optionCount = getItemCount(state.collection);
  const lastSize = useRef(optionCount);
  const lastOpen = useRef(state.isOpen);
  useEffect(() => {
    // Only announce the number of options available when the menu opens if there is no
    // focused item, otherwise screen readers will typically read e.g. "1 of 6".
    // The exception is VoiceOver since this isn't included in the message above.
    const didOpenWithoutFocusedItem =
      state.isOpen !== lastOpen.current &&
      (state.selectionManager.focusedKey == null || isAppleDevice());

    if (
      state.isOpen &&
      (didOpenWithoutFocusedItem || optionCount !== lastSize.current)
    ) {
      const announcement = stringFormatter.format('countAnnouncement', {
        optionCount,
      });
      announce(announcement);
    }

    lastSize.current = optionCount;
    lastOpen.current = state.isOpen;
  });

  // Announce when a selection occurs for VoiceOver. Other screen readers typically do this automatically.
  const lastSelectedKeys = useRef(mapSelectionToKeysSet(state.selectedKeys));

  useEffect(() => {
    if (isAppleDevice() && state.isFocused) {
      // Determine newly selected keys
      const selectedKeys = mapSelectionToKeysSet(state.selectedKeys);
      const addedKeys = [...selectedKeys].filter(
        (key) => !lastSelectedKeys.current.has(key),
      );

      // Make announcements for any new keys added to the selection
      addedKeys.forEach((key) => {
        const selectedItem = state.collection.getItem(key);

        if (selectedItem) {
          const optionText =
            selectedItem['aria-label'] || selectedItem.textValue || '';
          const announcement = stringFormatter.format('selectedAnnouncement', {
            optionText,
          });
          announce(announcement);
        }
      });

      // Update the ref to the new set of selected keys
      lastSelectedKeys.current = selectedKeys;
    }
  }, [state.selectedKeys, state.isFocused, state.collection, stringFormatter]);

  useEffect(() => {
    if (state.isOpen) {
      return ariaHideOutside(
        [inputRef.current, popoverRef.current].filter(
          (el) => el != null,
        ) as Element[],
      );
    }
  }, [state.isOpen, inputRef, popoverRef]);

  return {
    labelProps,
    buttonProps: {
      ...menuTriggerProps,
      ...triggerLabelProps,
      excludeFromTabOrder: true,
      onPress,
      onPressStart,
      isDisabled: isDisabled || isReadOnly,
    },
    inputProps: mergeProps(inputProps, {
      role: 'combobox',
      'aria-expanded': menuTriggerProps['aria-expanded'],
      'aria-controls': state.isOpen ? menuProps.id : undefined,
      // TODO: readd proper logic for completionMode = complete (aria-autocomplete: both)
      'aria-autocomplete': 'list',
      'aria-activedescendant': focusedItem
        ? getItemId(state, focusedItem.key)
        : undefined,
      onTouchEnd,
      // This disable's iOS's autocorrect suggestions, since the combo box provides its own suggestions.
      autoCorrect: 'off',
      // This disable's the macOS Safari spell check auto corrections.
      spellCheck: 'false',
    }),
    listBoxProps: mergeProps(menuProps, listBoxProps, {
      autoFocus: state.focusStrategy,
      shouldUseVirtualFocus: true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
      linkBehavior: 'selection' as const,
    }),
    descriptionProps,
    errorMessageProps,
  };
}
